'use client';

import React from 'react';
import { Download, Shuffle, AtSign } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Permissions {
  downloadAllowed: boolean;
  remixAllowed: boolean;
  creditsRequired: boolean;
}

interface PermissionTogglesProps {
  permissions: Permissions;
  onPermissionsChange: (p: Permissions) => void;
}

const permissionConfig = [
  {
    key: 'perm-download',
    field: 'downloadAllowed' as keyof Permissions,
    icon: Download,
    label: 'Downloads Allowed',
    desc: 'Editors can download the original file',
    warningIfOff: 'Clips with downloads disabled get 80% fewer interactions.',
  },
  {
    key: 'perm-remix',
    field: 'remixAllowed' as keyof Permissions,
    icon: Shuffle,
    label: 'Remix Allowed',
    desc: 'Others can create derivative edits from this clip',
    warningIfOff: null,
  },
  {
    key: 'perm-credits',
    field: 'creditsRequired' as keyof Permissions,
    icon: AtSign,
    label: 'Credits Required',
    desc: 'Users must credit your @username when publishing edits',
    warningIfOff: null,
  },
];

export default function PermissionToggles({ permissions, onPermissionsChange }: PermissionTogglesProps) {
  const toggle = (field: keyof Permissions) => {
    onPermissionsChange({ ...permissions, [field]: !permissions[field] });
  };

  return (
    <div className="space-y-3">
      {permissionConfig.map((perm) => {
        const Icon = perm.icon;
        const isOn = permissions[perm.field];
        return (
          <div key={perm.key}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    background: isOn ? 'rgba(108, 99, 255, 0.12)' : 'var(--muted)',
                    border: `1px solid ${isOn ? 'rgba(108, 99, 255, 0.3)' : 'var(--border)'}`,
                  }}
                >
                  <Icon size={14} style={{ color: isOn ? 'var(--primary)' : 'var(--muted-foreground)' }} />
                </span>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                    {perm.label}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {perm.desc}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggle(perm.field)}
                className={`toggle-track flex-shrink-0 ${isOn ? 'toggle-track-on' : ''}`}
                aria-checked={isOn}
                role="switch"
              >
                <span className={`toggle-thumb ${isOn ? 'toggle-thumb-on' : ''}`} />
              </button>
            </div>
            {!isOn && perm.warningIfOff && (
              <p
                className="text-[11px] mt-1.5 ml-10 pl-0.5"
                style={{ color: 'rgba(245, 158, 11, 0.8)' }}
              >
                ⚠ {perm.warningIfOff}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}